import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IProduct } from '~/Services/Product.interface'
import {
  ApiKeyClaimsResponse,
  ApiKeyStatus,
  CreateApiKeyDto,
  GetApiKeysParams,
  IApiKey,
  IApiKeyClaim,
  IApiKeyCreated,
  UpdateApiKeyDto,
} from '~/Services/ApiKey.interface'

// Product claims are SP_Claims — scoped to a (servicePrincipalId, productId)
// pair, since the same product's claims differ per Service Principal (they
// come from that SP's assigned Roles). Keyed as `${servicePrincipalId}:${productId}`.
interface ApiKeyState {
  isLoadingProducts: boolean
  products: IProduct[]
  productClaims: { [key: string]: IApiKeyClaim[] }
  loadingProductClaims: { [key: string]: boolean }
  isCreating: boolean
  createdApiKey?: IApiKeyCreated
  apiKeys: IApiKey[]
  isLoadingApiKeys: boolean
  isRevoking: boolean
  isUpdating: boolean
  apiKeyUpdated: boolean
  viewClaims?: ApiKeyClaimsResponse
  isLoadingClaims: boolean
  // List page filters — kept here (not component-local state) so they
  // survive navigating to New/Edit and back via history.goBack().
  listServicePrincipalId?: string
  listStatusFilter?: GetApiKeysParams['status']
}

const initialState: ApiKeyState = {
  isLoadingProducts: false,
  products: [],
  productClaims: {},
  loadingProductClaims: {},
  isCreating: false,
  apiKeys: [],
  isLoadingApiKeys: false,
  isRevoking: false,
  isUpdating: false,
  apiKeyUpdated: false,
  isLoadingClaims: false,
  // Default the list to the Active tab on first load; once the user changes
  // it, the choice is kept here so it survives navigating to New/Edit and back.
  listStatusFilter: ApiKeyStatus.Active,
}

export const ApiKeySlice = createSlice({
  name: 'apiKey',
  initialState,
  reducers: {
    getProducts: state => {
      state.isLoadingProducts = true
    },
    getProductsSuccess: (state, action: PayloadAction<IProduct[]>) => {
      state.products = action.payload
      state.isLoadingProducts = false
    },
    getProductsFail: state => {
      state.isLoadingProducts = false
    },
    getProductClaims: (
      state,
      action: PayloadAction<{
        servicePrincipalId: string
        productId: string
        audience: string
      }>,
    ) => {
      const { servicePrincipalId, productId } = action.payload
      state.loadingProductClaims[`${servicePrincipalId}:${productId}`] = true
    },
    getProductClaimsSuccess: (
      state,
      action: PayloadAction<{
        servicePrincipalId: string
        productId: string
        claims: IApiKeyClaim[]
      }>,
    ) => {
      const { servicePrincipalId, productId, claims } = action.payload
      const key = `${servicePrincipalId}:${productId}`
      state.productClaims[key] = claims
      state.loadingProductClaims[key] = false
    },
    getProductClaimsFail: (
      state,
      action: PayloadAction<{ servicePrincipalId: string; productId: string }>,
    ) => {
      const { servicePrincipalId, productId } = action.payload
      state.loadingProductClaims[`${servicePrincipalId}:${productId}`] = false
    },
    createApiKey: (
      state,
      action: PayloadAction<{
        servicePrincipalId: string
        data: CreateApiKeyDto
        productClaimIds: string[]
      }>,
    ) => {
      state.isCreating = true
    },
    createApiKeySuccess: (state, action: PayloadAction<IApiKeyCreated>) => {
      state.isCreating = false
      state.createdApiKey = action.payload
    },
    createApiKeyFail: state => {
      state.isCreating = false
    },
    resetCreatedApiKey: state => {
      state.createdApiKey = undefined
    },
    getApiKeys: (
      state,
      action: PayloadAction<{
        servicePrincipalId: string
        status?: GetApiKeysParams['status']
      }>,
    ) => {
      state.isLoadingApiKeys = true
    },
    getApiKeysSuccess: (state, action: PayloadAction<IApiKey[]>) => {
      state.apiKeys = action.payload
      state.isLoadingApiKeys = false
    },
    getApiKeysFail: state => {
      state.isLoadingApiKeys = false
    },
    revokeApiKey: (
      state,
      action: PayloadAction<{ servicePrincipalId: string; apiKeyId: string }>,
    ) => {
      state.isRevoking = true
    },
    revokeApiKeySuccess: state => {
      state.isRevoking = false
    },
    revokeApiKeyFail: state => {
      state.isRevoking = false
    },
    getApiKeyClaims: (state, action: PayloadAction<string>) => {
      state.isLoadingClaims = true
    },
    getApiKeyClaimsSuccess: (
      state,
      action: PayloadAction<ApiKeyClaimsResponse>,
    ) => {
      state.viewClaims = action.payload
      state.isLoadingClaims = false
    },
    getApiKeyClaimsFail: state => {
      state.isLoadingClaims = false
    },
    resetViewClaims: state => {
      state.viewClaims = undefined
    },
    updateApiKey: (
      state,
      action: PayloadAction<{
        apiKeyId: string
        servicePrincipalId: string
        data: UpdateApiKeyDto
        productClaimIds: string[]
      }>,
    ) => {
      state.isUpdating = true
      state.apiKeyUpdated = false
    },
    updateApiKeySuccess: state => {
      state.isUpdating = false
      state.apiKeyUpdated = true
    },
    updateApiKeyFail: state => {
      state.isUpdating = false
    },
    resetApiKeyUpdated: state => {
      state.apiKeyUpdated = false
    },
    setListServicePrincipalId: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.listServicePrincipalId = action.payload
    },
    setListStatusFilter: (
      state,
      action: PayloadAction<GetApiKeysParams['status']>,
    ) => {
      state.listStatusFilter = action.payload
    },
  },
})

export const ApiKeyActions = ApiKeySlice.actions
export const ApiKeyReducer = ApiKeySlice.reducer
